import React, { useState, useEffect } from 'react';
import Select from 'react-select'; 
import { motion, AnimatePresence } from 'framer-motion'; // 1. ADDED IMPORTS

const ApproveClientModal = ({ 
    isOpen, 
    onClose, 
    clientData, 
    options, 
    onConfirm, 
    currentUser 
}) => {
    // Form State
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [clientType, setClientType] = useState('new_client');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [error, setError] = useState('');

    // Sync state when modal opens or clientData changes
    useEffect(() => {
        if (isOpen && clientData) {
            setName(clientData.name || '');
            setPhone(clientData.phoneNumber || '');
            setClientType('new_client');
            setSelectedCustomer(null);
            setError('');
        }
    }, [isOpen, clientData]);

    // Handle form submission
    const handleApproveClick = () => {
        setError(''); 

        // 1. Validation
        if (!name.trim()) {
            setError('User Name cannot be empty.');
            return;
        }

        if (clientType === 'existing_client' && !selectedCustomer) {
            setError('Please search and select an existing customer.');
            return;
        }

        // Capture the exact time and admin details for consistency
        const adminName = currentUser?.name || currentUser?.email || "Admin";
        const currentTime = new Date().toISOString();

        // 2. Create the new history log entry
        const newActionLog = {
            status: "approved",
            by: adminName,
            at: currentTime
        };

        // 3. Prepare the payload
        const updatedClient = {
            ...clientData,
            name,
            phoneNumber: phone,
            status: "approved",
            client_type: clientType,
            
            // If existing, pass the full object stored in the select's 'value' property
            customer_details: clientType === 'existing_client' ? selectedCustomer.value : null,
            
            approved_by: adminName,
            approved_time: currentTime,

            // NEW: Append the approval log to the existing actions array
            actions: [...(clientData.actions || []), newActionLog]
        };

        // 4. Send back to parent
        onConfirm(updatedClient);
        console.log(updatedClient)
    };

    // 2. REMOVED the early return `if (!isOpen) return null;` 
    // AnimatePresence must stay in the DOM to handle the exit animation!

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div 
                    // Overlay Animation (Fades in/out)
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                >
                    <motion.div 
                        // Modal Box Animation (Pops up and scales in)
                        initial={{ scale: 0.8, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.8, opacity: 0, y: 20 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }} // Bouncy effect
                        className="bg-white rounded p-4 shadow-lg" 
                        style={{ width: '100%', maxWidth: '500px' }}
                    >
                        <h3 className="mb-3">Approve User</h3>
                        
                        <div className="text-start px-2 mt-3">
                            {/* Error Message */}
                            {error && <div className="alert alert-danger py-2">{error}</div>}

                            <div className="mb-3">
                                <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '14px' }}>User Name</label>
                                <input 
                                    className="form-control" 
                                    value={name} 
                                    onChange={(e) => setName(e.target.value)} 
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '14px' }}>Mobile Number</label>
                                <input 
                                    className="form-control" 
                                    value={phone} 
                                    onChange={(e) => setPhone(e.target.value)} 
                                />
                            </div>
                            
                            <div className="mb-2 mt-4 pt-3 border-top">
                                <label className="form-label text-muted fw-bold mb-2" style={{ fontSize: '14px' }}>Client Type</label>
                                <div className="d-flex gap-4 mb-3">
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="radio" 
                                            id="type-new" 
                                            checked={clientType === 'new_client'}
                                            onChange={() => setClientType('new_client')}
                                        />
                                        <label className="form-check-label fw-bold text-dark" htmlFor="type-new" style={{ cursor: 'pointer' }}>
                                            New Client
                                        </label>
                                    </div>
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="radio" 
                                            id="type-existing" 
                                            checked={clientType === 'existing_client'}
                                            onChange={() => setClientType('existing_client')}
                                        />
                                        <label className="form-check-label fw-bold text-dark" htmlFor="type-existing" style={{ cursor: 'pointer' }}>
                                            Existing Client
                                        </label>
                                    </div>
                                </div>

                                {/* REACT SELECT DROPDOWN - Only shows if Existing Client is checked */}
                                {clientType === 'existing_client' && (
                                    <div className="mb-3">
                                        <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '14px' }}>Search Existing Customer</label>
                                        <Select
                                            isClearable
                                            options={options || []}
                                            value={selectedCustomer}
                                            onChange={(val) => setSelectedCustomer(val)}
                                            placeholder="Search by name or number..."
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }} 
                                            menuPortalTarget={document.body}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <button className="btn btn-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button className="btn" style={{ backgroundColor: "#00C896", color: "white" }} onClick={handleApproveClick}>
                                Confirm & Approve
                            </button>
                        </div>
                    </motion.div> {/* 3. FIXED CLOSING TAGS */}
                </motion.div>     
            )}
        </AnimatePresence>
    );
};

export default ApproveClientModal;